import {
  HStack,
  LinearGradient,
  Link,
  LinkText,
  ScrollView,
  Text,
  View
} from "@gluestack-ui/themed";
import WayupLogo from "@repo/core/assets/images/wayup-logo.svg";
import { Button } from "@repo/core/components/core/Button";
import { Icon } from "@repo/core/components/core/Icon";
import { POLICY_URL } from "@repo/core/constants/global";
import { KeyboardAvoidingView } from "@repo/core/components/core/KeyboardAvoidingView";
import { useLoginController } from "@repo/core/screens/Login/controller";
import { LinearGradient as ExpoLinearGradient } from "expo-linear-gradient";
import { InputControlled } from "@repo/core/components/forms/InputControlled";
import {
  Platform,
  StatusBar as RNStatusBar,
  useWindowDimensions
} from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";

const gradientColors = ["$secondary950", "$primary800"];

function Login() {
  const { isLoggingIn, errorMessage, control, onSubmit, appVersion } =
    useLoginController();
  const isAndroid = Platform.OS === "android";
  const { height: windowHeight } = useWindowDimensions();

  const usernameField = (
    <InputControlled
      control={control}
      name="username"
      placeholder="Digite seu usuário"
      autoCorrect={false}
      spellCheck={false}
      autoCapitalize="none"
      autoComplete="username"
      textContentType="oneTimeCode"
      keyboardType="email-address"
      label="Usuário"
      isRequired
    />
  );

  const passwordField = (
    <InputControlled
      name="password"
      control={control}
      label="Senha"
      placeholder="Digite sua senha"
      type="password"
      returnKeyType="done"
      secureTextEntry
      autoCapitalize="none"
      autoCorrect={false}
      spellCheck={false}
      onKeyPress={(e) => {
        // NOTE: When pressed Enter on web
        if (e.keyCode === 13) {
          onSubmit();
        }
      }}
      onSubmitEditing={onSubmit}
      isRequired
    />
  );

  const errorMessageView = errorMessage ? (
    <HStack justifyContent="space-between" alignItems="center" gap="$2">
      <Icon name="alert-circle" color="$error700" size={16} />
      <Text w="$full" color="$error700" fontSize="$sm" flexShrink={1}>
        {errorMessage}
      </Text>
    </HStack>
  ) : null;

  const submitButton = (
    <Button
      text="Entrar"
      textStyle={{
        fontFamily: "inter",
        fontWeight: 600,
        fontSize: 16
      }}
      onPress={onSubmit}
      isLoading={isLoggingIn}
      mt="$4"
    />
  );

  const privacyLink = (
    <View alignItems="center" pt="$4" pb="$8" gap="$2">
      <Link href={POLICY_URL} isExternal>
        <LinkText color="$secondary950" textDecorationLine="none">
          Política de privacidade
        </LinkText>
      </Link>
    </View>
  );

  const appVersionText = (
    <Text fontSize="$sm" color="$secondary400">
      v{appVersion}
    </Text>
  );

  if (isAndroid) {
    return (
      <View flex={1}>
        <RNStatusBar barStyle={"light-content"} />
        <LinearGradient
          flex={1}
          // @ts-ignore: "colors" is documented and works but isn't typed for some reason
          colors={gradientColors}
          as={ExpoLinearGradient}
        >
          <KeyboardAwareScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ flexGrow: 1 }}
            keyboardShouldPersistTaps="handled"
            bottomOffset={24}
            extraKeyboardSpace={8}
            showsVerticalScrollIndicator={false}
          >
            <View
              w="$full"
              flexGrow={1}
              minHeight={windowHeight}
              justifyContent="space-between"
              alignItems="center"
            >
              <View w="$full" $base-p="$24" $xl-p="$32" alignItems="center">
                <WayupLogo />
              </View>
              <View
                w="$full"
                $base-pt="$8"
                $sm-pt="$10"
                pb="$4"
                flexGrow={1}
                alignItems="center"
                justifyContent="space-between"
                borderTopLeftRadius="$3xl"
                borderTopRightRadius="$3xl"
                backgroundColor="$white"
              >
                <View
                  $base-w="$full"
                  $base-px="$8"
                  $sm-maxWidth="$112"
                  gap="$6"
                >
                  {usernameField}
                  {passwordField}
                  {errorMessageView}
                  {submitButton}
                  {privacyLink}
                </View>
                {appVersionText}
              </View>
            </View>
          </KeyboardAwareScrollView>
        </LinearGradient>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView>
      <RNStatusBar barStyle={"light-content"} />
      <LinearGradient
        flex={1}
        // @ts-ignore: "colors" is documented and works but isn't typed for some reason
        colors={gradientColors}
        as={ExpoLinearGradient}
      >
        <ScrollView
          contentContainerStyle={{ flexGrow: 1 }}
          h="$full"
          keyboardShouldPersistTaps="handled"
        >
          <View
            w="$full"
            flexGrow={1}
            justifyContent="space-between"
            alignItems="center"
          >
            <View w="$full" $base-p="$24" $xl-p="$32" alignItems="center">
              <WayupLogo />
            </View>
            <View
              w="$full"
              $base-pt="$8"
              $sm-pt="$10"
              pb="$4"
              flexGrow={1}
              alignItems="center"
              justifyContent="space-between"
              borderTopLeftRadius="$3xl"
              borderTopRightRadius="$3xl"
              backgroundColor="$white"
            >
              <View $base-w="$full" $base-px="$8" $sm-maxWidth="$112" gap="$6">
                {usernameField}
                {passwordField}
                {errorMessageView}
                {submitButton}
                {privacyLink}
              </View>
              {appVersionText}
            </View>
          </View>
        </ScrollView>
      </LinearGradient>
    </KeyboardAvoidingView>
  );
}

export default Login;
